import React, { useState, useEffect } from 'react';
import { Loader2 } from 'lucide-react';
import PageHeader from '../components/PageHeader';
import DataTable from '../components/DataTable';
import ConfirmDialog from '../components/ConfirmDialog';
import { api } from '../../services/api';
import { useToast } from '../hooks/useToast';
import { reviews as dummyReviews } from '../data/dummyData';

const Reviews = () => {
  const { success, error: showError, warning } = useToast();
  const [reviewList, setReviewList] = useState(dummyReviews);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [confirmHide, setConfirmHide] = useState(false);
  const [selectedReview, setSelectedReview] = useState(null);
  const [hidingReview, setHidingReview] = useState(false);

  useEffect(() => {
    const loadReviews = async () => {
      try {
        setLoading(true);
        setError('');

        try {
          const response = await api.get('/admin/reviews');
          const apiReviews = (response.data?.data || []).map((review) => ({
            id: review._id,
            productName: review.productId?.name || 'Unknown Product',
            productId: review.productId?._id || review.productId,
            customerName: review.userId?.name || 'Anonymous',
            email: review.userId?.email || '',
            rating: review.rating || 0,
            review: review.review || '',
            date: new Date(review.createdAt).toLocaleDateString(),
            status: review.isHidden ? 'hidden' : 'approved',
            images: review.images || [],
          }));
          setReviewList(apiReviews);
        } catch (apiErr) {
          // Fall back to dummy data
          setReviewList(dummyReviews);
          if (apiErr.response?.status !== 404) {
            warning('Using sample data - API not available');
          }
        }
      } catch (err) {
        const msg = err.response?.data?.message || 'Failed to load reviews';
        setError(msg);
        warning(msg);
      } finally {
        setLoading(false);
      }
    };

    loadReviews();
  }, [warning]);

  const handleHideReview = (id) => {
    setSelectedReview(id);
    setConfirmHide(true);
  };

  const confirmHideReview = async () => {
    try {
      setHidingReview(true);
      const review = reviewList.find((r) => r.id === selectedReview);
      await api.patch(`/products/${review.productId}/reviews/${selectedReview}/hide`, {
        isHidden: true,
      });
      setReviewList((current) =>
        current.map((r) =>
          r.id === selectedReview ? { ...r, status: 'hidden' } : r
        )
      );
      success('Review hidden successfully');
    } catch (err) {
      const msg = err.response?.data?.message || 'Unable to hide review';
      showError(msg);
    } finally {
      setHidingReview(false);
    }
  };

  const columns = [
    { 
      key: 'productName', 
      label: 'Product', 
      sortable: true,
      render: (val) => (
        <div>
          <p className="font-medium text-gray-900 truncate">{val}</p>
        </div>
      )
    },
    { 
      key: 'customerName', 
      label: 'Customer', 
      sortable: true,
      render: (val, row) => (
        <div>
          <p className="font-medium text-gray-900">{val}</p>
          <p className="text-xs text-gray-500">{row.email}</p>
        </div>
      )
    },
    {
      key: 'rating',
      label: 'Rating',
      render: (val) => (
        <div className="flex items-center gap-1">
          {[...Array(5)].map((_, i) => (
            <span key={i} className={i < val ? 'text-yellow-400' : 'text-gray-300'}>
              ★
            </span>
          ))}
        </div>
      ),
      sortable: true,
    },
    { 
      key: 'review', 
      label: 'Review', 
      sortable: false,
      render: (val) => (
        <p className="text-sm text-gray-600 line-clamp-2">{val}</p>
      )
    },
    { key: 'date', label: 'Date', sortable: true },
    {
      key: 'status',
      label: 'Status',
      render: (val) => (
        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
          val === 'approved'
            ? 'bg-green-100 text-green-700'
            : 'bg-yellow-100 text-yellow-700'
        }`}>
          {val.charAt(0).toUpperCase() + val.slice(1)}
        </span>
      ),
    },
  ];

  return (
    <>
      <div className="space-y-6">
        <PageHeader
          title="Reviews & Ratings"
          description="Manage customer reviews"
          breadcrumbs={['Home', 'Reviews']}
        />

        {error && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-sm text-yellow-700">
            {error}
          </div>
        )}

        {loading ? (
          <div className="bg-white rounded-xl border border-gray-100 p-8 flex items-center justify-center">
            <Loader2 className="animate-spin text-amber-600 mr-2" size={20} />
            <span className="text-gray-600">Loading reviews...</span>
          </div>
        ) : (
          <DataTable
            columns={columns}
            data={reviewList}
            onDelete={handleHideReview}
            searchable={true}
            paginate={true}
            itemsPerPage={10}
          />
        )}
      </div>

      <ConfirmDialog
        isOpen={confirmHide}
        onClose={() => setConfirmHide(false)}
        onConfirm={confirmHideReview}
        title="Hide Review"
        message="Are you sure you want to hide this review?"
        confirmText={hidingReview ? "Hiding..." : "Hide"}
        isDangerous={false}
      />
    </>
  );
};

export default Reviews;
